/**
 * The advocate workspace's surface (EP-004C phase 3, + assignment authority
 * 2026-09-04).
 *
 * Every type mirrors what `advocateWorkspace.js` and
 * `advocateAssignmentService.js` return. The only writes are the two
 * manager acts, assign and bulk-assign; every other advocate action is
 * phase 5. Every list is paged.
 *
 * Vocabulary is version 1 and provisional. The labels below are how the
 * screens say those words to a person; the words are the business's.
 */
import { api, apiUpload, apiDownloadFile } from '../../services/api';

export type Stage = 'INTAKE' | 'VETTING' | 'VETTED' | 'READY_TO_SUBMIT' | 'SUBMITTED' | 'ENROLLED' | 'DISPOSITIONED' | 'SUSPENDED' | 'CLOSED' | 'UNKNOWN';
export type Disposition = 'ENROLLED' | 'DNQ' | 'NITWL' | 'C_NITWL' | 'PRIVATE_PAY'
  // R1 (Isaiah Cotto, 2026-10-01): handed back to the provider.
  | 'REEVALUATE_CURRENT_YEAR' | 'REEVALUATE_12_PLUS_MONTHS' | 'RESUBMIT_TO_PROVIDER';
export type Blocker = 'CANNOT_REACH_POA' | 'MEDICAID_PENDING' | 'PENDING_DOCUMENTATION' | 'UNABLE_TO_VET' | 'REVERSAL' | 'PAYOR_ISSUES' | 'FAMILY_UNDECIDED';
export type Lifecycle = 'DISCHARGED' | 'HOSPICE' | 'DECEASED' | 'FACILITY_DROPPED';
export type IntakeKind = 'TWL' | 'C_TWL' | 'REPLACEMENT' | 'REPLACEMENT_L' | 'REPLACEMENT_R' | 'REPLACEMENT_BOTH' | 'UNKNOWN';
export type ActionType = 'POA_FOLLOW_UP' | 'SUBMIT_UMED' | 'HANDLE_REVERSAL' | 'DOCUMENTATION_HANDOFF' | 'VET' | 'ASSIGN' | 'OBTAIN_VETTING_FROM_FACILITY' | 'CONTACT_RESPONSIBLE_PARTY'
  // A send-back landed on this case; "Mark handled" closes it (design §3.1).
  | 'RETURNED_TO_YOU';
export type AssigneeResolution = 'NONE' | 'RESOLVED' | 'UNRESOLVED_MONDAY_USER';
export type AttentionCode = 'OLD_LEAD' | 'AGE_UNKNOWN' | 'ACTION_PAST_DUE' | 'BLOCKED' | 'ASSIGNEE_UNRESOLVED' | 'STATUS_UNMAPPED';
export type Freshness = 'FRESH' | 'BEHIND' | 'STALE' | 'NEVER';

/** Who manages the resident's funds and pays the facility — the business's six words (April Torino). */
export type FinancialResponsibilityType = 'FACILITY_MANAGED' | 'FAMILY_MANAGED' | 'SELF_MANAGED' | 'GUARDIAN_MANAGED' | 'PRIVATE_PAY' | 'TRUSTEE_MANAGED';
export const FINANCIAL_RESPONSIBILITY_LABEL: Record<FinancialResponsibilityType, string> = {
  FACILITY_MANAGED: 'Facility managed', FAMILY_MANAGED: 'Family managed', SELF_MANAGED: 'Self managed',
  GUARDIAN_MANAGED: 'Guardian managed', PRIVATE_PAY: 'Private pay', TRUSTEE_MANAGED: 'Trustee managed',
};
/** `type` is null when the board says nothing or a word we do not know (raw kept). ACH on file is evidence, not a type. */
export interface FinancialResponsibility {
  type: FinancialResponsibilityType | null; raw: string | null; ach_on_file: boolean; mapped: boolean;
  /** Which system the value came from. EMERGE is an advocate's own record (2026-09-24). */
  source?: 'EMERGE' | 'MONDAY' | 'NONE';
  /** Set when eMerge holds one and the board still says something else. Shown, never hidden. */
  board_disagrees?: boolean;
  board_type?: FinancialResponsibilityType | null;
  recorded_at?: string; recorded_by?: string | null; note?: string | null;
}

/**
 * Saved operational lenses over My work and All cases — April Torino's
 * views (email 2026-09-07), kept as server-side filters over one set of
 * cases. The server owns the predicates (advocateWorkspace.LENSES); this is
 * what the screen calls them. Active is the default and is what the My work
 * count means. The lens once planned as "Sales" is now ENROLLMENTS
 * (addendum 2026-09-09); it is still absent because `enrolled_at` does not
 * exist yet and is generated by the UMED workflow, not chosen here.
 */
export type Lens =
  // A lens the server offers must be a key the client accepts. `new` and
  // `not-started` are 2026-09-23: what landed this week, and the INTAKE
  // stage no chip covered.
  | 'new' | 'not-started'
  | 'active' | 'needs-vetting' | 'vetted' | 'pending-documents'
  | 'twl-tbd' | 'cannot-reach-poa' | 'private-pay' | 'replacements' | 'unable-to-vet'
  | 'nitwl' | 'c-nitwl' | 'dnq' | 'deceased' | 'hospice' | 'discharged' | 'dropped-facility'
  | 'medicaid-pending' | 'c-twl' | 'reversal'
  | 'ready-to-submit' | 'enrolled'
  // Vetted, with or without pending documentation (2026-10-01).
  | 'in-progress'
  | 'old' | 'everything'
  // R8 (2026-10-01): All cases only -- closed work stays reviewable.
  | 'closed';
/**
 * How the More picker groups the rest (addendum §7, A-14): the whole
 * canonical vocabulary is reachable as a filter, in four groups and a time
 * pair. Filter vocabulary only — nothing here sets a status, and ENROLLED
 * least of all (A-8). Canonical labels: no board typos, no obsolete words.
 */
export type LensGroup = 'chip' | 'working' | 'outcomes' | 'exceptions' | 'umed' | 'time';
export const LENS_GROUPS: ReadonlyArray<{ key: Exclude<LensGroup, 'chip'>; label: string }> = [
  { key: 'working', label: 'Working' },
  { key: 'outcomes', label: 'Outcomes' },
  { key: 'exceptions', label: 'Exceptions' },
  { key: 'umed', label: 'UMED progression' },
  { key: 'time', label: 'By time' },
];
export const LENSES: ReadonlyArray<{ key: Lens; label: string; /** What the CHIP says when it is not the lens's own label. The key and the server's label never change. */ chip?: string; primary: boolean; group: LensGroup; what: string; allCasesOnly?: boolean }> = [
  // LensBar renders from THIS array, not from the server's catalog. A lens
  // the server offers and this list omits has no button at all -- which is
  // what happened to these two when they shipped on 2026-09-23.
  //
  // 2026-10-02: the chips are Active, New ("Newly assigned" until 2026-10-05), Needs vetting, Vetted,
  // Ready to submit, in that order. Vetted / In progress is under More, in
  // Working (Talhia: 157 vs 21; she calls from the 21). Every other lens is
  // under More, in its group. Labels are April's: only the chip's wording differs.
  { key: 'active', label: 'Active', primary: true, group: 'chip', what: 'Anything placed with you in the last 7 days and still open, plus not-started, vetted, TWL-TBD, private pay and replacements assigned within the last 45 days. Self managed has its own view: it is waiting on the family, not on you.' },
  // 2026-10-05: New is the status New, however long ago the lead was placed (the PAs: it leaves New when the status changes, not when a note is added or a week passes). The chip reads New, the word the PAs use and the status the row shows.
  { key: 'new', label: 'New', primary: true, group: 'chip', what: 'Every lead of yours whose status is New, however long ago it was placed. A note, a document or a facility correction keeps it here; recording a status moves it to the view its state belongs to.' },
  { key: 'needs-vetting', label: 'Needs vetting', primary: true, group: 'chip', what: 'Call the facility for vetting information.' },
  { key: 'vetted', label: 'Vetted', primary: true, group: 'chip', what: 'Contact the responsible party: review the test results, explain the program. Pending documentation is its own view.' },
  { key: 'ready-to-submit', label: 'Ready to submit', primary: true, group: 'chip', what: 'Vetting complete; the UMED can be submitted.' },

  { key: 'not-started', label: 'Not started', primary: false, group: 'working', what: 'Opened and placed with you, with no vetting begun yet. No date limit.' },
  { key: 'in-progress', label: 'Vetted / In progress', primary: false, group: 'working', what: 'Everything vetted, including cases waiting on documents. Overlaps Vetted and Self managed — documents needed.' },
  { key: 'pending-documents', label: 'Self managed — documents needed', primary: false, group: 'working', what: 'Vetted and self-managed, waiting on a signed UMED or ACH form.' },
  { key: 'twl-tbd', label: 'TWL-TBD', primary: false, group: 'working', what: 'The family has not decided yet; assigned within the last 45 days.' },
  { key: 'cannot-reach-poa', label: 'Cannot reach POA', primary: false, group: 'working', what: 'The decision-maker could not be reached; assigned within the last 45 days.' },
  { key: 'private-pay', label: 'Private pay', primary: false, group: 'working', what: 'The family chose to pay privately; assigned this month.' },
  { key: 'replacements', label: 'Replacements', primary: false, group: 'working', what: 'A replacement device; assigned this month.' },
  { key: 'unable-to-vet', label: 'Unable to vet', primary: false, group: 'working', what: 'Last resort: operations or the TM reaches the facility again; assigned this month.' },

  { key: 'nitwl', label: 'NITWL', primary: false, group: 'outcomes', what: 'Not interested, including C-NITWL; assigned this month.' },
  { key: 'c-nitwl', label: 'C-NITWL', primary: false, group: 'outcomes', what: 'Not interested, from a provider-listed resident; assigned this month.' },
  { key: 'dnq', label: 'DNQ', primary: false, group: 'outcomes', what: 'Did not qualify; assigned this month.' },
  { key: 'deceased', label: 'Deceased', primary: false, group: 'outcomes', what: 'Assigned this month.' },
  { key: 'hospice', label: 'Hospice', primary: false, group: 'outcomes', what: 'Assigned this month.' },
  { key: 'discharged', label: 'Discharged', primary: false, group: 'outcomes', what: 'Assigned this month.' },
  { key: 'dropped-facility', label: 'Dropped facility', primary: false, group: 'outcomes', what: 'The facility left the program; assigned this month.' },

  { key: 'medicaid-pending', label: 'Medicaid pending', primary: false, group: 'exceptions', what: 'Waiting on Medicaid, however long it takes.' },
  { key: 'c-twl', label: 'C-TWL', primary: false, group: 'exceptions', what: 'Not interested when the provider listed them; back to the provider with the PA note; assigned this month.' },
  { key: 'reversal', label: 'Reversal', primary: false, group: 'exceptions', what: 'A reversal is in the way of the case.' },

  { key: 'enrolled', label: 'Enrolled', primary: false, group: 'umed', what: 'The decision as recorded, whatever its date.' },

  { key: 'old', label: 'Old', primary: false, group: 'time', what: 'Unresolved and assigned 45 or more days ago.' },
  { key: 'everything', label: 'Everything', primary: false, group: 'time', what: 'Every open case assigned here, whatever its state.' },

  // R8 (2026-10-01): a disposition takes a case out of every working view; this
  // is where a manager still reviews it. All cases only -- My work is open work.
  { key: 'closed', label: 'Closed', primary: true, group: 'chip', allCasesOnly: true, what: 'Closed cases, newest first, with the disposition and the reason given when each closed.' },
];
export const isLens = (v: string | null | undefined): v is Lens => LENSES.some((l) => l.key === v);
/** A lens only All cases offers (Closed). Anywhere else it is not that queue's state. */
export const lensIsAllCasesOnly = (v: string | null | undefined): boolean => Boolean(LENSES.find((l) => l.key === v)?.allCasesOnly);

export const STAGE_LABEL: Record<Stage, string> = {
  INTAKE: 'New', VETTING: 'Vetting', VETTED: 'Vetted', READY_TO_SUBMIT: 'Ready to submit', SUBMITTED: 'Submitted',
  ENROLLED: 'Enrolled', DISPOSITIONED: 'Decided', SUSPENDED: 'On hold', CLOSED: 'Closed', UNKNOWN: 'Status not recognised',
};
export const DISPOSITION_LABEL: Record<Disposition, string> = {
  ENROLLED: 'Enrolled', DNQ: 'Does not qualify', NITWL: 'Not interested', C_NITWL: 'Not interested (C)', PRIVATE_PAY: 'Private pay',
  REEVALUATE_CURRENT_YEAR: 'Re-evaluate this year', REEVALUATE_12_PLUS_MONTHS: 'Re-evaluate after 12+ months', RESUBMIT_TO_PROVIDER: 'Resubmitted to the provider',
};
export const BLOCKER_LABEL: Record<Blocker, string> = {
  CANNOT_REACH_POA: 'Cannot reach POA', MEDICAID_PENDING: 'Medicaid pending', PENDING_DOCUMENTATION: 'Pending documentation',
  UNABLE_TO_VET: 'Unable to vet', REVERSAL: 'Reversal', PAYOR_ISSUES: 'Payor issues', FAMILY_UNDECIDED: 'Family undecided',
};
export const LIFECYCLE_LABEL: Record<Lifecycle, string> = {
  DISCHARGED: 'Discharged', HOSPICE: 'Hospice', DECEASED: 'Deceased', FACILITY_DROPPED: 'Facility dropped',
};
export const INTAKE_LABEL: Record<IntakeKind, string> = {
  TWL: 'TWL', C_TWL: 'C-TWL', REPLACEMENT: 'Replacement', REPLACEMENT_L: 'Replacement (L)', REPLACEMENT_R: 'Replacement (R)',
  REPLACEMENT_BOTH: 'Replacement (both)', UNKNOWN: 'Unknown intake',
};
/**
 * "Replacement · Left" for a replacement case, so it cannot be mistaken for a
 * new lead (2026-10-02). The side comes from the resident's request, else from
 * the intake kind's suffix; a replacement with neither says only "Replacement".
 */
export function replacementTag(c: { intake_kind: IntakeKind; replacement_side?: string | null }): string | null {
  if (!String(c.intake_kind || '').startsWith('REPLACEMENT')) return null;
  const side = c.replacement_side
    || (c.intake_kind === 'REPLACEMENT_L' ? 'LEFT' : c.intake_kind === 'REPLACEMENT_R' ? 'RIGHT' : c.intake_kind === 'REPLACEMENT_BOTH' ? 'BOTH' : null);
  const word = side === 'LEFT' ? 'Left' : side === 'RIGHT' ? 'Right' : side === 'BOTH' ? 'Both' : null;
  return word ? `Replacement · ${word}` : 'Replacement';
}
export const ACTION_LABEL: Record<ActionType, string> = {
  POA_FOLLOW_UP: 'Follow up with POA', SUBMIT_UMED: 'Submit UMED', HANDLE_REVERSAL: 'Handle reversal',
  DOCUMENTATION_HANDOFF: 'Hand off documentation', VET: 'Vet', ASSIGN: 'Assign',
  OBTAIN_VETTING_FROM_FACILITY: 'Call facility for vetting information', CONTACT_RESPONSIBLE_PARTY: 'Contact responsible party',
  RETURNED_TO_YOU: 'Returned to you',
};
export const ATTENTION_LABEL: Record<AttentionCode, string> = {
  OLD_LEAD: 'Old lead', AGE_UNKNOWN: 'No assignment date', ACTION_PAST_DUE: 'Past due', BLOCKED: 'Blocked',
  ASSIGNEE_UNRESOLVED: 'Assignee not on roster', STATUS_UNMAPPED: 'Status not recognised',
};

export interface OpenAction { id: string; action_type: ActionType; due_on: string | null; reason: string | null; owner_user_id: string | null }
export interface Attention { code: AttentionCode; detail: string }

/* ── EP-010: what the facility-grouped table carries on the row ─────────── */

/** What a Patient Liability can carry, against the two NEW totals (termsEngine). */
export type LiabilityClass = 'TWO_EAR_OK' | 'ONE_EAR_ONLY' | 'TOO_LOW' | 'UNKNOWN';
export const LIABILITY_CLASS_LABEL: Record<LiabilityClass, string> = {
  TWO_EAR_OK: 'Both ears', ONE_EAR_ONLY: 'One ear only', TOO_LOW: 'Below the floor', UNKNOWN: 'Not recorded',
};
export type LiabilityStatus = 'PROVISIONAL' | 'VERIFIED' | 'CORRECTION_PENDING' | 'SUPERSEDED' | 'UNKNOWN';
export const LIABILITY_STATUS_LABEL: Record<LiabilityStatus, string> = {
  PROVISIONAL: 'Stated', VERIFIED: 'Verified', CORRECTION_PENDING: 'Correction pending', SUPERSEDED: 'Superseded', UNKNOWN: 'Unknown',
};

/**
 * The CURRENT native liability fact. `null` means none exists — which is NOT
 * zero, and is not `patients.patient_liability` (the Monday mirror, which has
 * no author and no date). The table shows "Not recorded" and offers the edit.
 */
export interface LiabilityFact {
  fact_id: string;
  amount_cents: number;
  verification_status: LiabilityStatus;
  verification_source: 'MONDAY_MIRROR' | 'PA_ENTERED' | 'FINANCE_VERIFIED' | 'FACILITY_STATEMENT';
  effective_from: string | null;
  classification: LiabilityClass;
}

/** The newest note, truncated for the row. The full text is in the drawer's history. */
export interface LatestNote {
  id: string; kind: NoteKind; preview: string; truncated: boolean;
  recorded_at: string; recorded_by_name: string | null;
}

/** Which evidence exists, as facts rather than a count — "3 documents" does not say whether the audiogram is there. */
export interface DocumentState { bom_vetting: boolean; audiogram: boolean; umed_document: boolean; recording: boolean; total: number }

export type EnrollmentState = 'NOT_ENROLLED' | 'PENDING_CONFIRMATION' | 'ENROLLED' | 'NOT_ENROLLED_FINAL';
export const ENROLLMENT_LABEL: Record<EnrollmentState, string> = {
  NOT_ENROLLED: 'Not enrolled', PENDING_CONFIRMATION: 'Pending confirmation', ENROLLED: 'Enrolled', NOT_ENROLLED_FINAL: 'Did not enrol',
};
export type TermStatus = 'RESOLVED' | 'LIABILITY_TOO_LOW' | 'TERMS_UNRESOLVED';
export interface ResolvedTerm { status: TermStatus; months?: number; monthly_cents?: number; remainder_cents?: number; floor_cents?: number }
export interface SaleProduct { code: string; label: string; umed_type?: 'NEW' | 'REPLACEMENT'; ear_count?: 1 | 2 }

/** The sale on this case, if it has reached Ready to Submit. Enrolment is a state ON it, never a second sale. */
export interface CaseSale {
  id: string;
  closed_at: string;
  /** Dollars. `null` with `amount_pending_reason` set means no approved catalog priced it. */
  amount: number | null;
  amount_source: 'MONDAY_CLOSED_LEAD_TOTAL' | 'NATIVE_PRICING_CATALOG' | 'APPROVED_OVERRIDE' | null;
  product: SaleProduct | null;
  pricing_version: string | null;
  term: ResolvedTerm | null;
  enrollment_state: EnrollmentState;
  enrollment_confirmed_at: string | null;
  reversed_at: string | null;
  amount_pending_reason: 'PRICING_RULE_REQUIRED' | null;
}

/** EP-010 §4.1: one building's residents, with what the header needs to say. */
export interface FacilityGroup {
  facility_id: string | null;
  facility_name: string;
  residents: number;
  attention: number;
  due: number;
  items: WorkCase[];
  /** The number to call the building (Dominique Genua, 2026-09-24). */
  facility_phone?: string | null;
  /** The building's city: said in the header when two buildings share a name (2026-10-05). */
  facility_city?: string | null;
  /** Another group in this view carries the same name, so the header names the city too. */
  name_shared?: boolean;
  /** Two unmerged records with one name in one city: the record's short id tells them apart (a data steward may merge them). */
  facility_record_hint?: string;
  /** Only for a building larger than one page: this slice continues from, or onto, another page. */
  continued?: boolean;
  continues?: boolean;
}

export interface OtherOpenRecord {
  id: string;
  source_system: string | null;
  stage: Stage;
  facility_name: string | null;
  assignee_name: string | null;
}

export interface WorkCase {
  id: string;
  patient_id: string | null;
  /**
   * The resident's OTHER open lead rows, carried by the one shown.
   *
   * "The PAs are seeing duplicates" (2026-09-30): 202 residents hold 469 open
   * rows between them. The list shows one row per resident; the rest ride here
   * so they are visible rather than silently dropped.
   */
  other_open_records?: OtherOpenRecord[];
  facility_id: string | null;
  facility_name: string | null;
  intake_kind: IntakeKind;
  stage: Stage;
  disposition: Disposition | null;
  blockers: Blocker[];
  lifecycle: Lifecycle | null;
  source_status_raw: string | null;
  /** The word eMerge itself holds, once a PA has set one. Wins over the board's. */
  status_word?: string | null;
  source_file_status_raw: string | null;
  assignee_resolution: AssigneeResolution;
  assignment_source: 'MONDAY' | 'EMERGE';
  opened_on: string | null;
  assigned_on: string | null;
  /** EP-004P: the TWL Date (business clock), when eMerge first saw the case, why it is in intake, and the moment of the native assignment act. */
  twl_effective_at?: string | null;
  intake_observed_at?: string | null;
  intake_reason?: IntakeReason | null;
  assigned_at?: string | null;
  /** The side of a replacement, from the resident's replacement request (replacement cases only). */
  replacement_side?: 'LEFT' | 'RIGHT' | 'BOTH' | null;
  last_contact_on: string | null;
  closed_on: string | null;
  /** The word the case closed with (<=120 chars), when it is closed. */
  closed_reason?: string | null;
  /** All cases -> Closed only: the reason given on the transition that closed it (R8). */
  closing_reason?: string | null;
  vocabulary_version: number;
  assigned_advocate_user_id: string | null;
  assignee_name: string | null;
  first_name: string | null;
  last_name: string | null;
  patient_status: string | null;
  qualification_status: string[] | null;
  open_actions: OpenAction[];
  attention: Attention[];
  next_action: OpenAction | null;
  financial_responsibility: FinancialResponsibility;
  /**
   * EP-010: the four facts the operating table shows inline, so routine work
   * needs no navigation. Optional because `unassigned` and the exports share
   * the same row shape and do not select them — absent means "this query did
   * not ask", which is not the same as "there is none".
   */
  liability?: LiabilityFact | null;
  latest_note?: LatestNote | null;
  documents?: DocumentState;
  sale?: CaseSale | null;
}

/**
  * A resident whose UMED has been with DCF for 21 days without a proven approval
  * (EP-007, April Torino 2026-09-17). Approved means FLMMIS reflected; the board's
  * own "APPROVED" word is reported beside it, never instead of it.
  */
export interface FollowUp extends Omit<WorkCase, 'attention' | 'next_action'> {
  umed_case_id: string;
  umed_stage: string;
  noca_status: string;
  flmmis_status: string;
  went_to_dcf_on: string;
  board_approved_on: string | null;
  days_waiting: number;
  overdue_by: number;
}

/** An advocate sees no resident here; a manager does, because placing a case is deciding about a person. */
/** Why a case is in intake (EP-004P), from the resident's qualification and tags. */
export type IntakeReason = 'QUALIFIED' | 'SECOND_TIER' | 'MEDICAID_PENDING' | 'PRIVATE_PAY' | 'REPLACEMENT' | 'DNQ_REVIEW' | 'NO_MEDICAID' | 'NEED_SSN' | 'UNABLE_TO_ACCESS' | 'UNCLASSIFIED'
  // R6 (2026-10-01): did not qualify six or more months ago; placed again by the morning run.
  | 'DNQ_RECHECK';
export const INTAKE_REASON_LABEL: Record<IntakeReason, string> = {
  QUALIFIED: 'Qualified', SECOND_TIER: 'Qualified, 2nd tier', MEDICAID_PENDING: 'Medicaid pending', PRIVATE_PAY: 'Private pay', REPLACEMENT: 'Replacement',
  DNQ_REVIEW: 'DNQ — review', NO_MEDICAID: 'No Medicaid', NEED_SSN: 'Needs SSN', UNABLE_TO_ACCESS: 'Unable to access', UNCLASSIFIED: 'Not yet classified',
  DNQ_RECHECK: 'DNQ six months ago — placed again',
};
/** The intake lenses on Unassigned: April's daily reconciliation, natively. The business clock is the TWL Date. */
export type IntakeLens = 'new-today' | 'last-7-days' | 'all';
export const INTAKE_LENSES: ReadonlyArray<{ key: IntakeLens; label: string; what: string }> = [
  { key: 'new-today', label: 'New today', what: 'TWL Date today, or first seen by eMerge today when no TWL Date is recorded.' },
  { key: 'last-7-days', label: 'Last 7 days', what: 'TWL Date within the last 7 days, or first seen within 7 days when no TWL Date is recorded.' },
  { key: 'all', label: 'All', what: 'Every case waiting for an advocate, newest TWL first.' },
];
export const isIntakeLens = (v: string | null | undefined): v is IntakeLens => INTAKE_LENSES.some((l) => l.key === v);

export interface UnassignedCase {
  /** 2026-10-05: when ALREADY_SOLD is on the case, the resident's episode; `line` reads "Sold 07/20/26 · UMED approved". */
  episode?: { verdict: 'NEW_LEAD_OK' | 'ALREADY_SOLD' | 'UMED_IN_FLIGHT'; line: string | null; message: string | null } | null;
  id: string;
  /** M3 (2026-10-07): the case's building, else the resident's; `facility_from_resident` says which. */
  facility_id: string | null;
  facility_name: string | null;
  facility_from_resident?: boolean;
  /** M2 (2026-10-07): eMerge marks the resident deceased. Such a row is only ever listed beside the queue (not a lead). */
  resident_deceased?: boolean;
  /** #367 round 2: only Monday says so (status column, a board lifecycle). A marker; the row stays in the queue. */
  monday_says_deceased?: boolean;
  /** The case's intake exception codes; FACILITY_UNRESOLVED marks the building "(unconfirmed)". */
  intake_exceptions?: string[] | null;
  intake_kind: IntakeKind;
  stage: Stage;
  blockers: Blocker[];
  source_status_raw: string | null;
  opened_on: string | null;
  /** The TWL Date — the business clock. Null when the source never recorded one. */
  twl_effective_at: string | null;
  /** When eMerge first saw the case. Never the TWL Date. */
  intake_observed_at: string;
  intake_reason: IntakeReason | null;
  /** The resident's other cases (2026-10-02): the latest close, the word it closed with, and the latest assignment. */
  last_closed_on?: string | null;
  last_closed_word?: string | null;
  last_assigned_on?: string | null;
  assignee_resolution: AssigneeResolution;
  source_record_type: 'patient_advocate_leads' | 'unassigned_twls' | 'patients';
  source_assignee_monday_user_id: string | null;
  assignment_source: 'MONDAY' | 'EMERGE';
  version: number;
  patient_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  patient_status?: string | null;
  qualification_status?: string[] | null;
  /** What CounselEAR says about the resident (2026-10-01), and the same in words. */
  ce_verdict?: CounselearVerdict | null;
  ce_verdict_words?: string | null;
  /**
   * R7 (2026-10-01): Unassigned is one row per resident. The row is the case
   * that would be placed; the resident's other rows in this view ride here,
   * and `open_records` counts them all. Placing the row places them all.
   */
  other_open_records?: OtherOpenRecord[];
  open_records?: number;
}

export type CounselearVerdict =
  | 'UNLINKED' | 'NO_CE_RECORD' | 'DECEASED' | 'INACTIVE' | 'HOSPICE' | 'HAS_AIDS'
  | 'LEAD' | 'NOT_QUALIFIED' | 'LEAD_PENDING' | 'TWL_NOT_QUALIFIED' | 'QUALIFIED_NO_TWL' | 'NO_LEAD_TAGS';

export interface Paged<T> { items: T[]; total: number; page: number; limit: number }
export interface FollowUpPage extends Paged<FollowUp> { cadence_days: number }
/**
 * The filters the list was read through, and the buildings the PA may narrow
 * to (2026-10-01). `total` reflects the filters; the lens chips' counts do not.
 */
export interface WorkFilters {
  applied: Record<string, string | null>;
  facilities: Array<{ id: string; name: string; count: number }>;
}
/**
 * Pages break BETWEEN buildings (2026-10-05), so a page can hold a few more or
 * fewer residents than `limit`. `pages` and `range` say where this page sits;
 * an older server sends neither and the pager falls back to its own sums.
 */
export interface PageWindow { pages?: number; range?: { from: number; to: number } }
export interface WorkPage extends Paged<WorkCase>, PageWindow { awaiting_scope: number; lens: Lens; groups?: FacilityGroup[]; filters?: WorkFilters }
/** The manager's tenant-wide open list. Same table, same grouping, with the holder on each row. */
export interface AllCasesPage extends Paged<WorkCase>, PageWindow { lens: Lens; groups?: FacilityGroup[]; filters?: WorkFilters }
export interface UnassignedPage extends Paged<UnassignedCase> {
  summary: Array<{ reason: AssigneeResolution; monday_user_id: string | null; n: number }>;
  claim_authority: string;
  lens: IntakeLens;
  /** The whole queue, before this search or lens — so an empty result is never a false zero. */
  queue_total: number;
  lens_counts: Record<IntakeLens, number>;
  /** Open, unassigned cases eMerge cannot link to a resident: exceptions, not the queue. */
  unlinked: number;
  /**
   * Open and nobody's, but the RESIDENT is already an advocate's on another
   * row. Beside the queue from 2026-09-28, never in it: while these were in the
   * queue the ring could place the same resident with a second advocate, which
   * is how nineteen residents came to be held by two people at once.
   */
  held?: number;
  /** Open and nobody's, but CounselEAR says the resident is not a lead (2026-10-01). Beside the queue, never in it. */
  not_a_lead?: number;
  reason: 'NONE' | 'UNRESOLVED_MONDAY_USER' | 'UNLINKED' | 'HELD' | 'NOT_A_LEAD' | null;
}
export interface BulkPreview { dryRun: true; matched: number; assigned: 0; caseIds: string[]; excluded: { not_in_queue: number } }
export interface BulkResult { dryRun: false; matched: number; assigned: number; skipped: number; replayed: number; conflicted: number; notFound: number; careCreated?: number; noPatient?: number; conflicts: Array<{ caseId: string; currentOwner: string | null }> }

export interface Advocate {
  id: string; email: string; name: string; employee_id: string | null;
  manager: boolean; ratified: boolean; open_cases: number; open_actions: number;
}

/** What the PA works from on the lead record. The board's own words and dates are provenance, not here (addendum §8). */
export interface LeadEvidence {
  old_lead: boolean | null;
  responsible_party_name: string | null; responsible_party_email: string | null; responsible_party_phone: string | null; responsible_party_address: string | null;
  payor_issues: boolean | null; reason_for_nitwl: string | null; reason_for_dnq: string | null; notes: string | null; vetting_notes: string | null;
  poa_follow_up_needed: boolean | null;
}
/** A UMED request as the board holds it — provenance only. */
export interface Umed { id: string; status: string | null; faxed_status: string | null; submitted_date: string | null; sent_to_dcf_date: string | null; approval_date: string | null; last_follow_up_date: string | null }
/**
 * The UMED as eMerge reads it (EP-004U phase 1). The stage word is a source
 * claim; only FLMMIS reflected proves an approval (U-16) — the same rule as
 * the UMED workspace, so the two screens never disagree.
 */
export type UmedStage = 'CREATED' | 'NEEDS_DCF_ACCOUNT' | 'READY' | 'SUBMITTED' | 'APPROVED' | 'HANDED_TO_FINANCE' | 'CLOSED' | 'UNKNOWN';
export type UmedType = 'NEW' | 'REPLACEMENT' | 'CORRECTION' | 'CROSSOVER' | 'REVERSAL' | 'UNKNOWN';
export interface UmedCase {
  id: string; umed_type: UmedType; stage: UmedStage; exception: string; exception_state: string | null;
  noca_status: string; flmmis_status: string; readiness_blockers: string[];
  submitted_on: string | null; sent_to_dcf_on: string | null; approved_on: string | null; closed_on: string | null;
  /** 2026-10-06: the UMED operator moved the case to Approved (the approval act). Absent on a payload from before the deploy. */
  approved_by_act?: boolean;
  /** The approval act's own date; null without the act. `approved_on` is then only Monday's date (final verification P3). */
  act_approved_on?: string | null;
}
export const UMED_STAGE_LABEL: Record<UmedStage, string> = {
  CREATED: 'Created', NEEDS_DCF_ACCOUNT: 'Needs DCF account', READY: 'Ready to submit', SUBMITTED: 'Submitted to DCF',
  // Without the act this is Monday's claim, in the one name it has on every screen (final verification P3).
  APPROVED: 'Approved in Monday · not approved in eMerge', HANDED_TO_FINANCE: 'With Finance', CLOSED: 'Closed', UNKNOWN: 'Status not recognised',
};
export const UMED_TYPE_LABEL: Record<UmedType, string> = {
  NEW: 'New UMED', REPLACEMENT: 'Replacement', CORRECTION: 'Correction', CROSSOVER: 'Crossover', REVERSAL: 'Reversal', UNKNOWN: 'Type not recorded',
};
export interface CaseAction extends OpenAction { status: 'OPEN' | 'DONE' | 'CANCELLED'; source_rule: string; completed_at: string | null; created_at: string }
export interface CaseEvent { id: string; event_type: string; source_system: string; source_rule: string | null; detail: Record<string, unknown>; occurred_at: string; actor_name: string | null }
/**
 * D-30 (Kristian Sims and Talhia Zalvidea, 2026-10-02): a building's main
 * phone and the people the workforce calls, each line saying where it came
 * from -- the board (MONDAY) or a person in eMerge (EMERGE, with who, when
 * and why).
 */
export type ContactRole = 'MAIN' | 'BOM' | 'DON' | 'SSD' | 'ADMINISTRATOR' | 'MEDICAL_RECORDS' | 'OTHER';
export const CONTACT_ROLE_LABEL: Record<ContactRole, string> = {
  MAIN: 'Main phone', BOM: 'BOM', DON: 'DON', SSD: 'SSD', ADMINISTRATOR: 'Administrator', MEDICAL_RECORDS: 'Medical Records', OTHER: 'Other',
};
/** The roles a building is expected to have someone in, in reading order. */
export const PERSON_ROLES: ReadonlyArray<ContactRole> = ['BOM', 'DON', 'SSD', 'ADMINISTRATOR', 'MEDICAL_RECORDS'];
export interface FacilityContactLine {
  id?: string | null; name: string | null; title: string | null; email: string | null; phone: string | null;
  role?: ContactRole; source?: 'MONDAY' | 'EMERGE';
  recorded_by_name?: string | null; effective_from?: string | null; reason?: string | null;
  /** When the sync first brought a Monday line in (not when Monday last agreed). */
  imported_at?: string | null;
}
export interface MainPhone extends FacilityContactLine { monday_phone?: string | null }
export interface FacilityDirectory {
  facility_id: string; main_phone: MainPhone | null; contacts: FacilityContactLine[];
  by_role?: Record<string, FacilityContactLine[]>; missing_roles: ContactRole[];
}
export interface FacilityContactHistoryEntry {
  id: string; event_type: 'ADDED' | 'REPLACED' | 'ENDED' | 'MAIN_PHONE_SET' | 'MAIN_PHONE_ENDED'; role: ContactRole | null;
  before: Partial<FacilityContactLine> | null; after: Partial<FacilityContactLine> | null; reason: string; recorded_at: string; actor_name: string | null;
}
/** The building's operational context, brought to the case. Not patient data. */
export interface FacilityContext {
  id: string; name: string | null; phone: string | null; email: string | null; region: string | null; county: string | null;
  status: string | null; provider_next_visit_date: string | null; territory_manager: string | null;
  contacts: FacilityContactLine[];
  /** D-30: the main phone with its provenance; `phone` above is already the one to call. */
  main_phone?: MainPhone | null;
  missing_roles?: ContactRole[];
}
/**
 * The demographics record: the decision-maker fields (board: "POA") — whether
 * that is the healthcare surrogate is the business's call — and the date of
 * birth (A-10). No SSN field of any kind: the state of that requirement is
 * read from the UMED's readiness blockers, and the value is never sent (A-12).
 */
export interface Demographics {
  poa_name: string | null; poa_email: string | null; poa_phone: string | null; dob: string | null; ssn_on_file?: boolean;
  /** EP-007: numeric comes off the wire as a string; null when the record has none. */
  patient_liability?: string | number | null;
  patient_liability_checked_at?: string | null;
}

/* ── PA write enablement (2026-09-14) ─────────────────────────────────── */
export type StatusWord = 'TWL - VETTING' | 'TWL' | 'TWL-TBD/ACTIVE' | 'TWL-PENDING DOCUMENTATION' | 'CANNOT REACH POA' | 'MEDICAID PENDING' | 'PRIVATE PAY' | 'REPLACEMENT' | 'C-TWL' | 'UNABLE TO VET' | 'READY TO SUBMIT' | 'ENROLLED' | 'NITWL' | 'C-NITWL' | 'DNQ' | 'HOSPICE' | 'DECEASED' | 'DISCHARGED' | 'DROPPED FACILITY' | 'REVERSAL'
  | 'REEVALUATE - CURRENT YEAR' | 'REEVALUATE - 12+ MONTHS' | 'RESUBMIT TO PROVIDER'
  | 'NEEDS VERIFICATION';
export const STATUS_WORD_LABEL: Record<StatusWord, string> = {
  'TWL - VETTING': 'Vetting with the facility', 'TWL': 'Vetted — contact the responsible party', 'TWL-TBD/ACTIVE': 'Family undecided', 'TWL-PENDING DOCUMENTATION': 'Self managed — documents needed',
  'CANNOT REACH POA': 'Cannot reach the responsible party', 'MEDICAID PENDING': 'Medicaid pending', 'PRIVATE PAY': 'Private pay', 'REPLACEMENT': 'Replacement', 'C-TWL': 'Corporate TWL', 'UNABLE TO VET': 'Unable to vet',
  'READY TO SUBMIT': 'Ready to submit', 'ENROLLED': 'Enrolled', 'NITWL': 'Not interested (NITWL)', 'C-NITWL': 'Corporate not interested', 'DNQ': 'Does not qualify', 'HOSPICE': 'Hospice', 'DECEASED': 'Deceased', 'DISCHARGED': 'Discharged', 'DROPPED FACILITY': 'Facility dropped', 'REVERSAL': 'Reversal',
  'REEVALUATE - CURRENT YEAR': 'Re-evaluate this year (back to the provider)', 'REEVALUATE - 12+ MONTHS': 'Re-evaluate after 12+ months (back to the provider)', 'RESUBMIT TO PROVIDER': 'Resubmit to the provider',
  'NEEDS VERIFICATION': 'Needs verification (a VA checks benefits)',
};
/**
 * R1 (Isaiah Cotto, 2026-10-01): the words that hand the resident back to the
 * provider. Each closes the case and puts the resident on Manifest Review's
 * Resubmittals list, once.
 */
export const PROVIDER_WORDS: ReadonlyArray<StatusWord> = ['REEVALUATE - CURRENT YEAR', 'REEVALUATE - 12+ MONTHS', 'RESUBMIT TO PROVIDER'];
/**
 * D-29 (Dominique Genua 2026-10-02; April to confirm): a VA verifies the
 * resident's benefits. Closes the case; the VA's "Qualifies" returns it to
 * the PA who held it, "Does not qualify" closes it DNQ.
 */
export const VERIFICATION_WORDS: ReadonlyArray<StatusWord> = ['NEEDS VERIFICATION'];
/** Words that close the case. The server's `terminal_words` is the same list. */
export const TERMINAL_STATUS_WORDS: ReadonlyArray<StatusWord> = ['NITWL', 'C-NITWL', 'DNQ', ...PROVIDER_WORDS, ...VERIFICATION_WORDS, 'HOSPICE', 'DECEASED', 'DISCHARGED', 'DROPPED FACILITY'];
/** The words a closed lead can reopen at (the server's WORKING_WORDS). */
export const WORKING_STATUS_WORDS: ReadonlyArray<StatusWord> = ['TWL - VETTING', 'TWL', 'TWL-TBD/ACTIVE', 'TWL-PENDING DOCUMENTATION', 'CANNOT REACH POA', 'MEDICAID PENDING', 'PRIVATE PAY', 'REPLACEMENT', 'C-TWL', 'UNABLE TO VET'];
/**
 * D-31: a closed lead. A native closure is stage CLOSED; a closure projected
 * from the board is stage DISPOSITIONED with the decision on `disposition`
 * (PRIVATE_PAY is a working word, so it is not a closure).
 */
export const isClosedLead = (c: { stage: string; disposition?: string | null }): boolean =>
  c.stage === 'CLOSED' || (c.stage === 'DISPOSITIONED' && Boolean(c.disposition) && c.disposition !== 'PRIVATE_PAY');
/** What a reopening answers: where the case went and who holds it. */
export interface CaseReopened {
  caseId: string; word: string; stage: string; replayed: boolean;
  placement?: 'ACTOR' | 'PRIOR_HOLDER' | 'RESIDENT_PA' | 'LEADS_TO_PLACE'; holderUserId?: string | null; tag?: string | null; resubmittalsCancelled?: string[];
}
/** R2: a decision about the resident says why. The server refuses these without a reason (400 REASON_REQUIRED). D-29: the reason is what the VA checks. */
export const REASON_REQUIRED_WORDS: ReadonlyArray<StatusWord> = ['NITWL', 'C-NITWL', 'DNQ', ...PROVIDER_WORDS, ...VERIFICATION_WORDS];
/** A status word with the colour the board gives it. The hue is a label, never a claim. */
export interface StatusLegendEntry { word: string; label: string; color: string }

export type NoteKind = 'VETTING' | 'GENERAL';
/**
 * A status change. The resident's other open records take the same word
 * (`siblingsCarried`) unless the transition policy refuses it for that
 * record (`siblingsNotCarried`); both are absent on a replay.
 */
export interface StatusChanged {
  word: string; stage: string; version: number; closed: boolean; replayed: boolean;
  /** R1: the provider resubmittal a hand-back word asked for. `created: false` means one was already waiting. */
  resubmittal?: { id: string; state: 'PENDING' | 'PLACED' | 'CANCELLED'; created: boolean };
  /** D-29: the verification request NEEDS VERIFICATION opened. `created: false` means a VA already had the resident. */
  verification?: { id: string; state: 'OPEN' | 'QUALIFIED' | 'NOT_QUALIFIED'; created: boolean };
  siblingsCarried?: Array<{ caseId: string; transitionId: string }>;
  siblingsNotCarried?: Array<{ caseId: string; stage: string }>;
}
export type UploadClass ='FACE_SHEET' | 'UMED' | 'RECORDING' | 'DCF' | 'AUDIOGRAM' | 'BOM_VETTING' | 'ACH';
export const UPLOAD_CLASS_LABEL: Record<UploadClass, string> = { FACE_SHEET: 'Face sheet', UMED: 'UMED', RECORDING: 'Recording', DCF: 'DCF', AUDIOGRAM: 'Audiogram', BOM_VETTING: 'BOM vetting', ACH: 'ACH' };
export interface Transition { id: string; from_word: string | null; to_word: string; from_stage: string | null; to_stage: string; effective_at: string | null; recorded_at: string; source: 'EMERGE' | 'MONDAY'; reason: string | null; actor_name: string | null }
export interface CaseNote { id: string; kind: NoteKind; note: string; recorded_at: string; recorded_by_name: string | null }
export interface Correction { id: string; field: 'DOB' | 'SSN' | 'FACILITY'; prior_value: string | null; new_value: string | null; reason: string; recorded_at: string; actor_name: string | null }
export interface NativeCase {
  version: number; status_source: 'MONDAY' | 'EMERGE'; status_word: string | null; stage_changed_at: string | null; closed_reason: string | null; facility_source: 'MONDAY' | 'EMERGE';
  responsible_party: { name: string | null; email: string | null; phone: string | null; address: string | null; source: 'EMERGE' } | null;
  transitions: Transition[]; notes: CaseNote[]; corrections: Correction[];
}
export interface Readiness { state: 'READY' | 'READY_UNVERIFIED' | 'BLOCKED'; missing: string[]; untracked: string[]; documents: Record<string, boolean | null> }
/** T4 adds `record_liability`; its write group is `liability`. The server reports both held AND open. */
export type CanKey = 'change_status' | 'add_note' | 'update_responsible_party' | 'add_document' | 'correct_dob' | 'set_ssn' | 'transfer_facility' | 'ready_to_submit' | 'record_liability'
  // Who manages the funds (Talhia Zalvidea, 2026-09-24). Shares the
  // responsible-party capability: same parties, same advocate.
  | 'record_financial_responsibility'
  // Correct the RESIDENT's building everywhere (2026-10-05): the facility capability, the transfer group.
  | 'correct_facility';
export const freshKey = (): string => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
/** The board's own record of the lead: reconciliation facts, for reviewers. */
export interface BoardRecord {
  status: string | null; file_status: string | null; date_assigned: string | null; last_follow_up_date: string | null;
  poa_follow_up_date: string | null; date_closed: string | null; reversal_notification_status: string | null; sfs_rep: string | null;
}
/** Present only when the server decided the caller may review provenance. */
export interface Provenance {
  source_system: string; source_record_type: string; vocabulary_version: number;
  source_status_raw: string | null; source_file_status_raw: string | null;
  assignment_source: 'MONDAY' | 'EMERGE'; assignment_note: string | null; assignment_transferred_at: string | null;
  board: BoardRecord | null; umed_requests: Umed[];
}
/** EP-009: how the viewer relates to the case they opened. FORMER is read-only; history is not ownership. */
export interface ViewerRelationship {
  relationship: 'MANAGER' | 'CURRENT' | 'FORMER' | 'NONE';
  read_only: boolean;
  intervals: Array<{ assigned_at: string; unassigned_at: string | null; confidence: 'EXACT' | 'SOURCE_REPORTED' }>;
}

/** EP-009 Monthly Sales: one credited sale in the selected close month. */
export interface SalesRow {
  id: string; case_id: string; resident: string | null; counselear_id: string | null; facility_name: string | null;
  twl_effective_at: string | null;
  first_assigned_at: string | null; first_assigned_confidence: 'EXACT' | 'SOURCE_REPORTED' | null;
  sale_closed_at: string; sale_close_source: 'EMERGE_TRANSITION' | 'MONDAY_DATE_CLOSED'; sale_close_confidence: 'EXACT' | 'SOURCE_REPORTED';
  sale_amount: number | null; amount_source: 'MONDAY_CLOSED_LEAD_TOTAL' | 'NATIVE_PRICING_CATALOG' | 'APPROVED_OVERRIDE' | null;
  assignment_origin: 'PRIOR_MONTH' | 'SAME_MONTH' | 'UNKNOWN';
  credit_basis: 'HOLDER_AT_CLOSE' | 'CURRENT_HOLDER_FALLBACK' | 'NONE'; reversed_at: string | null;
  stage: Stage; disposition: Disposition | null; lifecycle: Lifecycle | null; blockers: Blocker[];
  umed_stage: string | null; flmmis_status: string | null;
  /** EP-010: what was sold, priced by which catalog, termed by which schedule. All null on a projected Monday row. */
  product: SaleProduct | null;
  pricing_version: string | null;
  term_policy_version: string | null;
  term: ResolvedTerm | null;
  liability_cents_at_close: number | null;
  /** A state ON this sale. A later enrolment never creates a second row. */
  enrollment: { state: EnrollmentState; confirmed_at: string | null; basis: 'FLMMIS_REFLECTED' | 'VA_CONFIRMED' | 'SOURCE_REPORTED' | null };
  amount_pending_reason: 'PRICING_RULE_REQUIRED' | null;
}
export interface SalesPage {
  month: string; timezone: string; attribution_rule_version: string; attribution_ruling: string; viewing_user_id: string;
  summary: {
    sales_closed: number; sale_value: number; amount_pending: number; prior_month_assignments: number; origin_unknown: number; reversed: number;
    enrolled: number; awaiting_enrollment: number; pricing_rule_required: number;
  };
  items: SalesRow[];
}

/* ── EP-010 §5.3: preparing the submittal ──────────────────────────────── */

export interface OfferedProduct {
  code: string; label: string; umed_type: 'NEW' | 'REPLACEMENT'; ear_count: 1 | 2;
  /** Cents. `null` when no approved catalog prices it — then `sellable` is false and the reason is on `catalog`. */
  total_cents: number | null;
  itemisation: Array<{ label: string; cents: number; quantity?: number }>;
  itemisation_inferred: boolean;
  /** The lowest monthly the longest term produces. Below it, no term fits. */
  floor_cents: number | null;
  term: ResolvedTerm | null;
  sellable: boolean;
}
export interface Submittal {
  catalog: {
    status: 'ACTIVE' | 'NO_ACTIVE_VERSION';
    version: string | null;
    term_policy_version: string;
    approved_by?: string | null;
    approval_reference?: string | null;
    approved_at?: string | null;
    reason: 'PRICING_RULE_REQUIRED' | null;
  };
  terms_rule_version: string;
  liability_cents: number | null;
  liability_classification: LiabilityClass;
  products: OfferedProduct[];
  liability: LiabilityFact | null;
  readiness: Readiness | null;
}
export const ORIGIN_LABEL: Record<SalesRow['assignment_origin'], string> = { PRIOR_MONTH: 'Prior-month assignment', SAME_MONTH: 'This month', UNKNOWN: 'Assignment date unavailable' };

/** EP-009 All Cases: every case the advocate ever held, with their relationship to it. */
export type HistoryFilter = 'all' | 'active' | 'sold' | 'pending-documents' | 'closed' | 'dnq-nitwl';
export const HISTORY_FILTERS: ReadonlyArray<HistoryFilter> = ['all', 'active', 'sold', 'pending-documents', 'closed', 'dnq-nitwl'];
export const isHistoryFilter = (v: string | null): v is HistoryFilter => v !== null && (HISTORY_FILTERS as readonly string[]).includes(v);
export interface HistoryRow extends WorkCase {
  relationship: 'CURRENT' | 'FORMER';
  my_first_assigned_at: string | null; my_last_unassigned_at: string | null;
  last_event: { type: string; at: string } | null;
}
export interface HistoryPage extends Paged<HistoryRow> {
  filter: HistoryFilter; filters: Array<{ key: HistoryFilter; label: string; count: number }>; ever_held: number; viewing_user_id: string;
}

export interface CaseDetail {
  case: WorkCase & { source_record_type: string; assignment_note: string | null };
  /** The resident's CURRENT native Patient Liability, or null. The detail route sends it beside the case. */
  liability?: LiabilityFact | null;
  viewer?: ViewerRelationship;
  evidence: LeadEvidence | null; umed_cases: UmedCase[]; actions: CaseAction[]; events: CaseEvent[]; facility: FacilityContext | null;
  demographics: Demographics | null;
  native?: NativeCase;
  documents?: CaseDocument[];
  allowed_next_words?: StatusWord[];
  provenance?: Provenance;
  /** Where eMerge says the RESIDENT lives, and on whose say-so (2026-10-05). Null when no relationship is current. */
  resident_facility?: ResidentFacility | null;
}
export interface ResidentFacility {
  relationship_id: string; facility_id: string; facility_name: string | null; facility_city: string | null;
  method: string; resolved_by_type: 'HUMAN' | 'SYSTEM' | 'SOURCE'; source_system: string | null; resolved_at: string | null;
  decided_by_name: string | null; decided_in_emerge: boolean;
}

/* ── The resident's note thread (2026-10-05) ─────────────────────────── */

export type NoteSource = 'PA' | 'UMED' | 'FINANCE';
export interface ResidentNote {
  id: string; source: NoteSource;
  /** PA: VETTING | GENERAL. UMED: UMED. Finance: NOTE | CALL | PAYMENT_ARRANGEMENT | RECONCILIATION. */
  kind: string;
  note: string;
  /** Postgres text, microseconds and Z: also the cursor's clock. */
  recorded_at: string;
  author: { id: string | null; name: string | null; role: string | null };
  case_id: string | null;
  episode: { case_id: string | null; label: string; open: boolean | null };
  provenance: 'EMERGE';
}
export interface ResidentNotesPage {
  patient_id: string;
  items: ResidentNote[];
  total: number;
  next_cursor: string | null;
  q: string | null;
  monday_history: Array<{ case_id: string; label: string; notes: string | null; vetting_notes: string | null }>;
  episodes: Array<{ case_id: string; label: string; open: boolean }>;
  writable_cases: Array<{ case_id: string; label: string }>;
  sources: { pa: boolean; umed: boolean; finance: boolean };
  read_only: boolean;
}
export type FacilityChangeKind = 'CORRECTION' | 'MOVE';
export interface FacilityCorrected {
  correctionId: string; relationshipId: string | null; kind: FacilityChangeKind; facilityId: string; facilityName: string; facilityCity: string | null;
  fromFacilityId?: string | null; casesMoved: number; umedCasesMoved: number | null; version: number | null;
  /** Null on a replay: the first answer already said who covers the building. */
  coverage: { facilityPaUserId: string | null; facilityPaName: string | null; holderMatches: boolean | null } | null;
  replayed: boolean;
}
export interface CaseDocument { id: string; document_class: string; original_filename: string | null; content_type: string; byte_size: number; source_system: 'MONDAY' | 'EMERGE'; source_created_at: string | null; imported_at: string | null; created_at: string; version: number }

export interface Counts {
  my_work: number; awaiting_scope: number; follow_ups: number; unassigned?: number; unassigned_unlinked?: number; all_open?: number;
  lenses: Partial<Record<Lens, number>>;
  intake_lenses?: Partial<Record<IntakeLens, number>>;
  last_projection_at: string | null;
  /** The sync watermark the last projection read — what "board read" means. */
  board_read_at: string | null;
  source_synced_at: string | null;
  freshness: Freshness;
}
export interface Scope {
  advocate: boolean;
  manager: boolean;
  /** May see Source details — the server's decision, on the UMED workspace's review capability. */
  provenance_visible?: boolean;
  viewing_user_id: string;
  counts: Counts;
  writes: 'NOT_AVAILABLE' | 'ASSIGNMENT_ONLY' | 'NATIVE';
  write_groups?: string[];
  /** What this caller may do, when writes are NATIVE: held and open. The server decides again on every call. */
  can?: Partial<Record<CanKey, boolean>> & {
    /** Signed send-back to the previous holder (docs/record-flow/send-back-design.md §4.1): held AND its own gate open AND the key configured. Not a write act, so not a CanKey. */
    send_back?: boolean;
  };
  status_words?: StatusWord[];
  /** One governed colour per status word. The row and the legend read the same list. */
  status_legend?: StatusLegendEntry[];
  /** R2 (2026-10-01): which words close the case, and which of those need a reason. */
  terminal_words?: StatusWord[];
  reason_required_words?: StatusWord[];
  document_classes?: UploadClass[];
  /** T3 (charter §33): whether the CounselEAR side is keeping up; a notice only when it changes what to do. */
  source_health?: { source: 'COUNSELEAR'; state: 'HEALTHY' | 'STALE' | 'DEGRADED' | 'FAILED' | 'UNKNOWN'; reasons: string[]; notice: { tone: 'critical' | 'warning'; text: string } | null } | null;
}
export const canDo = (s: Scope, what: CanKey): boolean => s.writes === 'NATIVE' && Boolean(s.can && s.can[what]);

export interface ApiResult<T> { success: boolean; data?: T; error?: string; code?: string; message?: string; status?: number; retryAfterSeconds?: number }

export interface ListQuery { q?: string; page?: number; as?: string | null; facility?: string | null; reason?: string | null; lens?: Lens | IntakeLens | null }

/**
 * What My work and Team cases can be narrowed by, beyond the lens. Empty is
 * "not set": it is dropped from the query and from the URL. The server answers
 * a value it does not know with 400 INVALID_FILTER.
 */
export interface Refine { facility: string; status: string; fr: string; liability: string; docs: string; sort: string }
export const NO_REFINE: Refine = { facility: '', status: '', fr: '', liability: '', docs: '', sort: '' };
export const REFINE_KEYS = {
  work: ['facility', 'status', 'fr', 'liability', 'docs', 'sort'],
  all: ['facility', 'status', 'sort'],
} as const;
export type RefineKey = keyof Refine;
export const LIABILITY_FILTERS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'TWO_EAR_OK', label: 'Two-ear OK' }, { value: 'ONE_EAR_ONLY', label: 'One ear only' },
  { value: 'TOO_LOW', label: 'Too low' }, { value: 'UNKNOWN', label: 'Not checked' },
];
export const DOCS_FILTERS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'ready', label: 'UMED and audiogram on file' }, { value: 'missing-umed', label: 'Missing UMED' },
  { value: 'missing-audiogram', label: 'Missing audiogram' }, { value: 'missing-recording', label: 'Missing recording' },
  { value: 'none', label: 'None on file' },
];
export const FR_FILTERS: ReadonlyArray<{ value: string; label: string }> = [
  ...(Object.keys(FINANCIAL_RESPONSIBILITY_LABEL) as FinancialResponsibilityType[]).map((k) => ({ value: k, label: FINANCIAL_RESPONSIBILITY_LABEL[k] })),
  { value: 'UNKNOWN', label: 'Unknown' },
];
const SORT_ALL: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'assigned', label: 'Date assigned, oldest' }, { value: 'assigned-desc', label: 'Date assigned, newest' },
  { value: 'last-contact', label: 'Last contact' }, { value: 'facility', label: 'Building' },
];
/** `''` is Priority, the server's default (`rank`), and is never sent. */
export const SORTS: Record<'work' | 'all', ReadonlyArray<{ value: string; label: string }>> = {
  work: [
    ...SORT_ALL.slice(0, 3), { value: 'age', label: 'Lead age' }, SORT_ALL[3],
    { value: 'liability', label: 'Patient Liability' }, { value: 'due', label: 'Next action due' },
  ],
  all: SORT_ALL,
};
/** The refine values this view may carry; anything else in a URL is dropped rather than sent to be refused. */
export function readRefine(view: 'work' | 'all', get: (k: string) => string | null): Refine {
  const out = { ...NO_REFINE };
  const pick = (k: RefineKey, ok: (v: string) => boolean) => { const v = (get(k) || '').trim(); if ((REFINE_KEYS[view] as readonly string[]).includes(k) && v && ok(v)) out[k] = v; };
  pick('facility', (v) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v));
  pick('status', (v) => v.length <= 120);
  pick('fr', (v) => FR_FILTERS.some((o) => o.value === v));
  pick('liability', (v) => LIABILITY_FILTERS.some((o) => o.value === v));
  pick('docs', (v) => DOCS_FILTERS.some((o) => o.value === v));
  pick('sort', (v) => SORTS[view].some((o) => o.value === v));
  return out;
}

function qs(params: Record<string, string | number | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * A resident the search found, whatever state their work is in. Team cases
 * answers "whose open work is this"; this answers "what does eMerge hold
 * about this person" (packet §17).
 */
export type ResidentSituation = 'OPEN_HELD' | 'OPEN_UNASSIGNED' | 'CLOSED_ONLY' | 'NO_CASE';

export interface ResidentHit {
  id: string;
  first_name: string | null;
  last_name: string | null;
  /** The name eMerge holds, or null when it holds none. Never invented. */
  name: string | null;
  name_source: 'RESIDENT' | 'NONE';
  counselear_id: string | null;
  facility_id: string | null;
  facility_name: string | null;
  resident_status: string | null;
  tags: string[];
  twl_date: string | null;
  last_test_on: string | null;
  situation: ResidentSituation;
  situation_words: string;
  cases_total: number;
  cases_open: number;
  latest_case: { id: string; stage: string; status_word: string | null; opened_on: string | null; closed_on: string | null; closed_reason: string | null; advocate: string | null } | null;
  has_umed: boolean;
  service_history: { enrolled: boolean; devices: boolean; umed: boolean };
}

export const advocateApi = {
  /**
   * Who manages the resident's funds (Talhia Zalvidea, 2026-09-24).
   * Written to eMerge's own record; the board's column is MONDAY-owned.
   */
  recordFinancialResponsibility: (caseId: string, body: { type: FinancialResponsibilityType; achOnFile: boolean; note: string | null }) =>
    api.post(`/advocate/cases/${caseId}/financial-responsibility`, {
      type: body.type, ach_on_file: body.achOnFile, note: body.note,
    }),

  /** Every resident matching the words, open case or not, held or not. */
  searchResidents: async (l: { q: string; page?: number }): Promise<ApiResult<Paged<ResidentHit> & { q: string }>> =>
    (await api.get(`/advocate/residents/search${qs({ q: l.q, page: l.page })}`)) as ApiResult<Paged<ResidentHit> & { q: string }>,
  /**
   * The list the screen is showing, as a CSV the browser saves. Resolves to
   * an error message to show, or null when the file arrived. The server
   * decides the rows and the filename; this passes the filters on screen.
   */
  exportWork: async (l: ListQuery): Promise<string | null> => apiDownloadFile('/advocate/work/export', { as: l.as, q: l.q, lens: l.lens as string }),
  exportHistory: async (l: ListQuery & { filter?: HistoryFilter | null }): Promise<string | null> => apiDownloadFile('/advocate/history/export', { as: l.as, q: l.q, filter: l.filter }),
  exportUnassigned: async (l: ListQuery): Promise<string | null> => apiDownloadFile('/advocate/unassigned/export', { q: l.q, lens: l.lens as string, reason: l.reason, facility: l.facility }),
  exportAll: async (l: ListQuery): Promise<string | null> => apiDownloadFile('/advocate/all/export', { as: l.as, q: l.q, lens: l.lens as string, facility: l.facility }),
  scope: async (as?: string | null): Promise<ApiResult<Scope>> => (await api.get(`/advocate${qs({ as })}`)) as ApiResult<Scope>,
  advocates: async (): Promise<ApiResult<Advocate[]>> => (await api.get('/advocate/advocates')) as ApiResult<Advocate[]>,
  work: async (l: ListQuery & Partial<Refine>): Promise<ApiResult<WorkPage>> => (await api.get(`/advocate/work${qs({ as: l.as, q: l.q, lens: l.lens, page: l.page, facility: l.facility, status: l.status, fr: l.fr, liability: l.liability, docs: l.docs, sort: l.sort })}`)) as ApiResult<WorkPage>,
  followUps: async (l: ListQuery): Promise<ApiResult<FollowUpPage>> => (await api.get(`/advocate/follow-ups${qs({ as: l.as, q: l.q, page: l.page })}`)) as ApiResult<FollowUpPage>,
  unassigned: async (l: ListQuery): Promise<ApiResult<UnassignedPage>> => (await api.get(`/advocate/unassigned${qs({ q: l.q, page: l.page, facility: l.facility, reason: l.reason, lens: l.lens })}`)) as ApiResult<UnassignedPage>,
  all: async (l: ListQuery & Partial<Refine>): Promise<ApiResult<AllCasesPage>> => (await api.get(`/advocate/all${qs({ as: l.as, q: l.q, lens: l.lens, page: l.page, facility: l.facility, status: l.status, sort: l.sort })}`)) as ApiResult<AllCasesPage>,
  /** EP-009: the selected close month's credited sales (own, or another advocate's for a manager). */
  sales: async (l: { as?: string | null; month?: string | null }): Promise<ApiResult<SalesPage>> => (await api.get(`/advocate/sales${qs({ as: l.as, month: l.month })}`)) as ApiResult<SalesPage>,
  /** EP-009: every case the advocate ever held. */
  history: async (l: ListQuery & { filter?: HistoryFilter | null }): Promise<ApiResult<HistoryPage>> => (await api.get(`/advocate/history${qs({ as: l.as, q: l.q, filter: l.filter, page: l.page })}`)) as ApiResult<HistoryPage>,
  case: async (id: string): Promise<ApiResult<CaseDetail>> => (await api.get(`/advocate/cases/${id}`)) as ApiResult<CaseDetail>,
  /** EP-004P: `expected_from` is the holder the screen last saw (null = nobody); a placement made since is refused, never overwritten. */
  assign: async (id: string, advocateUserId: string | null, note?: string, expectedFrom?: string | null, idempotencyKey?: string): Promise<ApiResult<{ changed: boolean; replayed?: boolean; toUserId: string | null }>> =>
    (await api.post(`/advocate/cases/${id}/assign`, { advocate_user_id: advocateUserId, note, ...(expectedFrom === undefined ? {} : { expected_from: expectedFrom }), ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}) })) as ApiResult<{ changed: boolean; replayed?: boolean; toUserId: string | null }>,
  bulkAssign: async (body: { monday_user_id?: string | null; reason?: string | null; from_advocate_user_id?: string | null; facility_id?: string | null; case_ids?: string[]; q?: string | null; lens?: IntakeLens | null; advocate_user_id: string | null; note?: string; dry_run: boolean; idempotency_key?: string }):
    Promise<ApiResult<BulkPreview | BulkResult>> =>
    (await api.post('/advocate/assignments/bulk', body)) as ApiResult<BulkPreview | BulkResult>,
  /* PA write enablement: each act its own call; each 404s on a process where its group is closed. */
  facilities: async (q: string): Promise<ApiResult<{ items: { id: string; name: string; city: string | null; state: string | null }[] }>> => (await api.get(`/advocate/facilities${qs({ q })}`)) as ApiResult<{ items: { id: string; name: string; city: string | null; state: string | null }[] }>,
  readiness: async (id: string): Promise<ApiResult<Readiness>> => (await api.get(`/advocate/cases/${id}/readiness`)) as ApiResult<Readiness>,
  changeStatus: async (id: string, body: { status: StatusWord; reason?: string; effective_at?: string; version: number; idempotency_key: string }): Promise<ApiResult<StatusChanged>> =>
    (await api.post(`/advocate/cases/${id}/status`, body)) as ApiResult<StatusChanged>,
  reopenCase: async (id: string, body: { reason: string; word?: StatusWord; idempotency_key: string }): Promise<ApiResult<CaseReopened> & { detail?: { caseId?: string; duplicateOf?: string | null } }> =>
    (await api.post(`/advocate/cases/${id}/reopen`, body)) as ApiResult<CaseReopened> & { detail?: { caseId?: string; duplicateOf?: string | null } },
  addNote: async (id: string, body: { kind: NoteKind; note: string; idempotency_key: string }): Promise<ApiResult<{ noteId: string; recordedAt?: string; replayed: boolean }>> =>
    (await api.post(`/advocate/cases/${id}/notes`, body)) as ApiResult<{ noteId: string; recordedAt?: string; replayed: boolean }>,
  responsibleParty: async (id: string, body: { name?: string; email?: string; phone?: string; address?: string; version: number }): Promise<ApiResult<{ version: number }>> =>
    (await api.post(`/advocate/cases/${id}/responsible-party`, body)) as ApiResult<{ version: number }>,
  correctDob: async (id: string, body: { dob: string; reason: string; idempotency_key: string }): Promise<ApiResult<{ dob: string; changed?: boolean; replayed: boolean }>> =>
    (await api.post(`/advocate/cases/${id}/dob`, body)) as ApiResult<{ dob: string; changed?: boolean; replayed: boolean }>,
  /** Sent once, over TLS, to a no-store route; never echoed back, never stored in component state beyond the field. */
  setSsn: async (id: string, body: { ssn: string; reason: string; idempotency_key: string }): Promise<ApiResult<{ state: 'SET' | 'REPLACED'; replayed: boolean }>> =>
    (await api.post(`/advocate/cases/${id}/ssn`, body)) as ApiResult<{ state: 'SET' | 'REPLACED'; replayed: boolean }>,
  /** The resident's whole note thread, newest page first; `before` pages back in time. */
  residentNotes: async (patientId: string, l: { before?: string | null; q?: string | null; limit?: number } = {}): Promise<ApiResult<ResidentNotesPage>> =>
    (await api.get(`/advocate/residents/${patientId}/notes${qs({ before: l.before || undefined, q: l.q || undefined, limit: l.limit })}`)) as ApiResult<ResidentNotesPage>,
  /** Correct the RESIDENT's building: relationship, pointer, every open case and UMED (2026-10-05). */
  correctFacility: async (id: string, body: { facility_id: string; kind: FacilityChangeKind; reason: string; effective_at?: string; version: number; expected_relationship_id: string | null; idempotency_key: string }): Promise<ApiResult<FacilityCorrected>> =>
    (await api.post(`/advocate/cases/${id}/facility/correct`, body)) as ApiResult<FacilityCorrected>,
  transferFacility: async (id: string, body: { facility_id: string; reason: string; effective_at?: string; version: number; idempotency_key: string }): Promise<ApiResult<{ facilityId: string; facilityName: string; version: number; replayed: boolean }>> =>
    (await api.post(`/advocate/cases/${id}/facility`, body)) as ApiResult<{ facilityId: string; facilityName: string; version: number; replayed: boolean }>,
  uploadDocument: async (id: string, documentClass: UploadClass, file: File, idempotencyKey: string): Promise<ApiResult<{ documentId: string; documentClass: string; version: number; deduplicated: boolean }>> => {
    const form = new FormData();
    form.append('document_class', documentClass);
    form.append('idempotency_key', idempotencyKey);
    form.append('file', file);
    return (await apiUpload(`/advocate/cases/${id}/documents`, form)) as unknown as ApiResult<{ documentId: string; documentClass: string; version: number; deduplicated: boolean }>;
  },
  /**
   * A file too large for the request path goes straight to S3 (2026-09-21).
   *
   * The server chooses where it lands and hands back a URL that can create
   * that one object; the browser PUTs the bytes; the server then verifies what
   * it now owns (size, S3's own checksum, and the file's first bytes) before it
   * counts as a document. A recording of a 40-minute call cannot travel the
   * ordinary route: the web server refuses a body over 25 MB.
   */
  uploadDocumentDirect: async (
    id: string,
    documentClass: UploadClass,
    file: File,
    onProgress?: (fraction: number) => void,
  ): Promise<ApiResult<{ documentId: string; version: number; deduplicated: boolean }>> => {
    const ticket = await api.post(`/advocate/cases/${id}/documents/upload-url`, {
      document_class: documentClass,
      content_type: file.type || 'application/octet-stream',
      filename: file.name,
      byte_size: file.size,
    }) as ApiResult<{ ticket_id: string; url: string; headers: Record<string, string> }>;
    if (!ticket.success || !ticket.data) return ticket as unknown as ApiResult<never>;

    const put = await new Promise<{ ok: boolean; status: number }>((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', ticket.data!.url, true);
      for (const [k, v] of Object.entries(ticket.data!.headers || {})) xhr.setRequestHeader(k, v);
      if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
      xhr.onload = () => resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status });
      xhr.onerror = () => resolve({ ok: false, status: 0 });
      xhr.send(file);
    });
    if (!put.ok) {
      return { success: false, code: 'UPLOAD_FAILED', error: 'The file did not finish uploading. Nothing was added.' } as unknown as ApiResult<never>;
    }
    return (await api.post(`/advocate/cases/${id}/documents/complete`, { ticket_id: ticket.data.ticket_id })) as ApiResult<{ documentId: string; version: number; deduplicated: boolean }>;
  },
  documentUrl: async (id: string, documentId: string): Promise<ApiResult<{ url: string; expires_in: number }>> =>
    (await api.post(`/advocate/cases/${id}/documents/${documentId}/url`, {})) as ApiResult<{ url: string; expires_in: number }>,
  /**
   * T4: the advocate states the Patient Liability learned while vetting.
   * Always PROVISIONAL / PA_ENTERED — verification is Finance's route. The
   * answer is the CURRENT fact, which stays Finance's verified one when this
   * statement disagrees with it; then `correctionPending` is true and
   * `written` is the row waiting for Finance.
   */
  recordLiability: async (id: string, body: { amount_cents: number; effective_from?: string | null; reason?: string | null; idempotency_key: string }):
    Promise<ApiResult<{ fact: LiabilityFact | null; written: LiabilityFact | null; changed: boolean; replayed: boolean; correctionPending?: boolean }>> =>
    (await api.post(`/advocate/cases/${id}/liability`, body)) as ApiResult<{ fact: LiabilityFact | null; written: LiabilityFact | null; changed: boolean; replayed: boolean; correctionPending?: boolean }>,
  /**
   * D-30: a building's directory, its recent history, and whether this
   * caller may change it (FACILITY_CONTACT_WRITE in scope, and the
   * `contacts` group open). Its own route, not the advocate workspace's.
   */
  facilityContacts: async (facilityId: string): Promise<ApiResult<{ directory: FacilityDirectory; history: FacilityContactHistoryEntry[]; can: { write: boolean } }>> =>
    (await api.get(`/facilities/${facilityId}/contacts`)) as ApiResult<{ directory: FacilityDirectory; history: FacilityContactHistoryEntry[]; can: { write: boolean } }>,
  /** Add a contact in a role, replace one (`replaces_contact_id`), or correct the main phone (role MAIN). */
  recordFacilityContact: async (facilityId: string, body: {
    role: ContactRole; name?: string | null; title?: string | null; phone?: string | null; email?: string | null;
    reason: string; replaces_contact_id?: string | null; idempotency_key: string;
  }): Promise<ApiResult<{ record: { id: string }; replayed: boolean; directory: FacilityDirectory }>> =>
    (await api.post(`/facilities/${facilityId}/contacts`, body)) as ApiResult<{ record: { id: string }; replayed: boolean; directory: FacilityDirectory }>,
  /** "No longer there." */
  endFacilityContact: async (facilityId: string, contactId: string, reason: string): Promise<ApiResult<{ ended: string; directory: FacilityDirectory }>> =>
    (await api.post(`/facilities/${facilityId}/contacts/${contactId}/end`, { reason })) as ApiResult<{ ended: string; directory: FacilityDirectory }>,
  /** EP-010 §5.3: what may be sold, at what price, on what term, and whether the evidence is in. */
  submittal: async (id: string): Promise<ApiResult<Submittal>> => (await api.get(`/advocate/cases/${id}/submittal`)) as ApiResult<Submittal>,
  prepareReadyDraft: async (id: string, body: PAProposal): Promise<ApiResult<{ draft: PAReadyDraft }>> =>
    (await api.post(`/advocate/cases/${id}/ready-draft`, body)) as ApiResult<{ draft: PAReadyDraft }>,
  previewReadyDraft: async (id: string, body: PAProposal & { draft_id: string }): Promise<ApiResult<{ draft: PAReadyDraft; url: string; access_event_id: string }>> =>
    (await api.post(`/advocate/cases/${id}/ready-draft/preview`, body)) as ApiResult<{ draft: PAReadyDraft; url: string; access_event_id: string }>,
  reviewReadyDraft: async (id: string, body: PAProposal & { draft_id: string; sha256: string; input_fingerprint: string; preview_access_id: string }): Promise<ApiResult<{ reviewId: string }>> =>
    (await api.post(`/advocate/cases/${id}/ready-draft/review`, body)) as ApiResult<{ reviewId: string }>,
  readyToSubmit: async (id: string, body: {
    version: number; idempotency_key: string; product_code?: string | null;
    review_id: string;
    /** EP-010: a custom amount for a deal the catalog does not describe. Both halves or neither. */
    override_amount_cents?: number | null; override_reason?: string | null;
  }): Promise<ApiResult<ReadyToSubmitResult> & { detail?: { blockingUmeds?: BlockingUmed[] } & Record<string, unknown> }> =>
    (await api.post(`/advocate/cases/${id}/ready-to-submit`, body)) as ApiResult<ReadyToSubmitResult> & { detail?: { blockingUmeds?: BlockingUmed[] } & Record<string, unknown> },
  /** Private pay: the case's latest private-pay handoff (a returned one included) and the refusals known before a product is chosen. */
  privatePay: async (id: string): Promise<ApiResult<PrivatePayStatus>> => (await api.get(`/advocate/cases/${id}/private-pay`)) as ApiResult<PrivatePayStatus>,
  /** Private pay goes straight to Finance: one act, no UMED. Both confirmations are the PA's, and the server refuses without them. */
  privatePayToFinance: async (id: string, body: {
    confirm_private_pay: true; not_spend_down: true; product_code: string;
    /** Isaiah, 2026-10-07: the total is the catalog price. The PA chooses paid in full, or a monthly plan of 2..12 months. No override_* keys. */
    payment_plan: 'PAID_IN_FULL' | 'MONTHLY'; months?: number; version?: number; idempotency_key?: string;
  }): Promise<ApiResult<PrivatePayResult>> =>
    (await api.post(`/advocate/cases/${id}/private-pay`, body)) as ApiResult<PrivatePayResult>,
};

export interface PrivatePayHandoff {
  id: string; state: string; label: 'Private pay (no UMED)'; handed_at: string; handed_by_name: string | null; sale_id: string;
  terms: { total_cents: number; term_months: number; monthly_cents: number; remainder_cents: number; rule_version: string | null; pricing_version: string | null };
  /** Set when Finance sent it back to the PA (state RETURNED_TO_UMED on the wire; never shown with those words). */
  returned: null | { at: string; reason: string | null; by_name: string | null };
}
export interface PrivatePayStatus {
  case_id: string;
  handoff: PrivatePayHandoff | null;
  blockers: { code: string; message: string }[];
  /** The approved catalog's prices; null means no approved catalog is in effect (PRICING_VERSION_MISSING). */
  catalog?: null | { version: string; products: { code: string; label: string; total_cents: number }[] };
  plans?: { kinds: ['PAID_IN_FULL', 'MONTHLY']; monthly_min: number; monthly_max: number; rule_version: string };
}
export interface PrivatePayResult {
  handoffId: string; saleId: string; caseId: string; state: 'RECEIVED'; label: 'Private pay (no UMED)'; totalCents: number;
  terms: { termMonths: number; monthlyCents: number; remainderCents: number; ruleVersion: string | null };
  replayed: boolean; siblingsClosed: number; siblingsKept: number; version: number;
}

/**
 * #367 round 6: a UMED a Ready to submit was refused over (409 ALREADY_SOLD / UMED_ALREADY_IN_PROGRESS), by id, its
 * state in the UMED board's words, the day it was last touched, and whether this advocate may open it.
 */
export interface BlockingUmed {
  umedCaseId: string; stage: string; stageWords: string; lastUpdated: string | null; inFlight: boolean; done: boolean; viewable?: boolean;
}

/** One act: the transition, the UMED handoff and the sale. `saleId` is present from the first call. */
export interface ReadyToSubmitResult {
  stage: string; umedCaseId: string | null; umedCreated: boolean; version: number; replayed: boolean;
  saleId?: string | null; saleCreated?: boolean;
  amountCents?: number | null;
  amountSource?: 'NATIVE_PRICING_CATALOG' | 'APPROVED_OVERRIDE' | null;
  amountPendingReason?: 'PRICING_RULE_REQUIRED' | null;
  /** The resident's other open records, closed as duplicates of this one; those with their own sale or UMED stay open. */
  siblingsClosed?: Array<{ caseId: string; transitionId: string }>;
  siblingsKept?: Array<{ caseId: string; sales: number; umed: number }>;
}

export interface PAProposal {
  product_code: string | null;
  override_amount_cents?: number | null;
  override_reason?: string;
}
export interface PAReadyDraft {
  id: string; sha256: string; inputFingerprint: string; generatedAt: string; pageCount: number; manifestSha256: string;
}

/** A person's display name, without inventing one when both parts are absent. */
export function personName(p: { first_name?: string | null; last_name?: string | null }): string {
  const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
  return name || 'Name not recorded';
}

/** Calendar days, read as the UTC date — the same rule as the provider portal (#97). */
function calendarDay(value: string | null | undefined): { y: number; m: number; d: number } | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return { y: parsed.getUTCFullYear(), m: parsed.getUTCMonth(), d: parsed.getUTCDate() };
}
export function shortDate(value: string | null | undefined): string {
  const c = calendarDay(value);
  if (!c) return '—';
  return new Date(Date.UTC(c.y, c.m, c.d)).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
}
/** An instant (a sync, a projection), in the viewer's own time — not a board date. */
export function shortDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
export function daysFromToday(value: string | null | undefined): number | null {
  const c = calendarDay(value);
  if (!c) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  return Math.round((new Date(c.y, c.m, c.d).getTime() - today) / 86400000);
}
export function relativeDay(value: string | null | undefined): string | null {
  const days = daysFromToday(value);
  if (days === null) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}
